import {
  FloatingPortal,
  Placement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react"
import { ReactNode, useMemo, useState } from "react"

import { classNames } from "../../../../lib/classNames"

export interface FloatingTooltipProps {
  trigger: ReactNode
  content: ReactNode
  placement?: Placement
  className?: string
  offsetDistance?: number
  isDisabled?: boolean
  openDelay?: number
  variant?: "simple" | "complex"
  portal?:
    | { renderTo: "inline" }
    | { renderTo: "document" }
    | { renderTo: "custom"; root: HTMLElement }
}

export function FloatingTooltip({
  trigger,
  content,
  placement = "bottom-end",
  className = "",
  offsetDistance = 4,
  isDisabled = false,
  openDelay = 0,
  variant = "simple",
  portal = { renderTo: "inline" },
}: FloatingTooltipProps) {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [flip(), offset(offsetDistance)],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    delay: { open: openDelay },
    handleClose: safePolygon(),
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  const portalRoot = useMemo(() => {
    if (portal.renderTo === "custom") return portal.root
    if (portal.renderTo === "document") return document.body
    return undefined
  }, [portal])

  if (isDisabled) return trigger

  const tooltip = (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      className={classNames(
        variant === "simple"
          ? "bg-neutral-bold-default color-inverse"
          : "bg-elevation-surface-default color-default",
        "shadow-surface z-20 whitespace-pre-line rounded px-3 py-2",
        className
      )}
      data-testid="floating-tooltip-content"
      {...getFloatingProps()}
    >
      {content}
    </div>
  )

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        data-testid="floating-tooltip"
      >
        {trigger}
      </div>
      {isOpen &&
        (portalRoot ? (
          <FloatingPortal root={portalRoot}>{tooltip}</FloatingPortal>
        ) : (
          tooltip
        ))}
    </>
  )
}
